import { createFromJS, Precision } from "../../../../types";
import calendarTypes from "../../../../types/calendarTypes";
import floatToInt from "../../../../utils/floatToInt";
import { getEraDuration,getEraIsBackwards } from "../selectors";
import dateByAddingSpan from "./dateByAddingSpan";
import { calculateMonthsSinceEraStart } from "./dateInternalCalculations";

// TODO: These will fail tests

/**
 * @param {CalendarRecord} calendar
 * @param {DateRecord} date1
 * @param {DateRecord} date2
 * @returns {TimespanRecord}
 */
const absoluteTimespanBetweenDates = (calendar, date1, date2) => {
  let span = null,
    testDate = null;

  // Calculate years gap
  const years = yearsBetweenDates(calendar, date1, date2);
  // Now calculate date when we add that span
  span = createFromJS.TimespanRecord({ years });
  testDate = dateByAddingSpan(calendar, date1, span);

  // Early bail out if we are exactly correct
  if (testDate.timestamp === date2.timestamp) {
    return span;
  }

  // Otherwise proceed with others...
  const months = monthsBetweenDates(calendar, testDate, date2);
  span = createFromJS.TimespanRecord({ years, months });
  testDate = dateByAddingSpan(calendar, date1, span);
  if (testDate.timestamp === date2.timestamp) {
    return span;
  }

  const weeks = weeksBetweenDates(calendar, testDate, date2);
  span = createFromJS.TimespanRecord({ years, months, weeks });
  testDate = dateByAddingSpan(calendar, date1, span);
  if (testDate.timestamp === date2.timestamp) {
    return span;
  }

  const days = daysBetweenDates(calendar, testDate, date2);
  span = createFromJS.TimespanRecord({ years, months, weeks, days });
  testDate = dateByAddingSpan(calendar, date1, span);
  if (testDate.timestamp === date2.timestamp) {
    return span;
  }

  const hours = hoursBetweenDates(testDate, date2);
  span = createFromJS.TimespanRecord({ years, months, weeks, days, hours });
  testDate = dateByAddingSpan(calendar, date1, span);
  if (testDate.timestamp === date2.timestamp) {
    return span;
  }

  const minutes = minutesBetweenDates(testDate, date2);
  span = createFromJS.TimespanRecord({
    years,
    months,
    weeks,
    days,
    hours,
    minutes
  });
  testDate = dateByAddingSpan(calendar, date1, span);
  if (testDate.timestamp === date2.timestamp) {
    return span;
  }
  const seconds = secondsBetweenDates(testDate, date2);
  return createFromJS.TimespanRecord({
    years,
    months,
    weeks,
    days,
    hours,
    minutes,
    seconds
  });
};

/**
 * @param {CalendarRecord} calendar
 * @param {DateRecord} date1
 * @param {DateRecord} date2
 * @returns {TimespanRecord}
 */
const relativeTimespanBetweenDates = (calendar, date1, date2) => {
  let span = null,
    testDate = date1;

  let weeks = 0;
  if (calendar.calendarType === calendarTypes.CALENDAR_TYPE_WEEKDAY_TIME) {
    weeks = weeksBetweenDates(calendar, testDate, date2);
    span = createFromJS.TimespanRecord({ weeks });
    testDate = dateByAddingSpan(calendar, date1, span);
    if (testDate.timestamp === date2.timestamp) {
      return span;
    }
  }

  let days = 0;
  if (calendar.calendarType <= calendarTypes.CALENDAR_TYPE_DAY_TIME) {
    days = daysBetweenDates(calendar, testDate, date2);
    span = createFromJS.TimespanRecord({ weeks, days });
    testDate = dateByAddingSpan(calendar, date1, span);
    if (testDate.timestamp === date2.timestamp) {
      return span;
    }
  }

  const hours = hoursBetweenDates(testDate, date2);
  span = createFromJS.TimespanRecord({ weeks, days, hours });
  testDate = dateByAddingSpan(calendar, date1, span);
  if (testDate.timestamp === date2.timestamp) {
    return span;
  }

  const minutes = minutesBetweenDates(testDate, date2);
  span = createFromJS.TimespanRecord({ weeks, days, hours, minutes });
  testDate = dateByAddingSpan(calendar, date1, span);
  if (testDate.timestamp === date2.timestamp) {
    return span;
  }
  const seconds = secondsBetweenDates(testDate, date2);
  return createFromJS.TimespanRecord({ weeks, days, hours, minutes, seconds });
};

// Timespan from date1 to date2
const timespanBetweenDates = (calendar, date1, date2) => {
  if (calendar.calendarType === calendarTypes.CALENDAR_TYPE_DATE_TIME) {
    return absoluteTimespanBetweenDates(calendar, date1, date2);
  } else {
    return relativeTimespanBetweenDates(calendar, date1, date2);
  }
};

const unitsBetweenDates = (calendar, date1, date2, precision) => {
  switch (precision) {
    case Precision.Year:
      return yearsBetweenDates(calendar, date1, date2);
    case Precision.Month:
      return monthsBetweenDates(calendar, date1, date2);
    case Precision.Week:
      return weeksBetweenDates(calendar, date1, date2);
    case Precision.Day:
      return daysBetweenDates(calendar, date1, date2);
    case Precision.Hour:
      return hoursBetweenDates(date1, date2);
    case Precision.Minute:
      return minutesBetweenDates(date1, date2);
    case Precision.Second:
      return secondsBetweenDates(date1, date2);
    default:
      return null;
  }
};

const weeksBetweenTS = (calendar, ts1, ts2) =>
  floatToInt(secondsBetweenTS(ts1, ts2) / (calendar.get("weekdays").count() * calendar.get("hoursInDay") * 3600));

const daysBetweenTS = (calendar, ts1, ts2) =>
  floatToInt(secondsBetweenTS(ts1, ts2) / (calendar.get("hoursInDay") * 3600));
const hoursBetweenTS = (ts1, ts2) => floatToInt(secondsBetweenTS(ts1, ts2) / 3600);
const minutesBetweenTS = (ts1, ts2) => floatToInt(secondsBetweenTS(ts1, ts2) / 60);
const secondsBetweenTS = (ts1, ts2) => ts2 - ts1;

const yearsBetweenDates = (calendar, date1, date2) =>
  floatToInt(monthsBetweenDates(calendar, date1, date2) / calendar.get("months").count());

const monthsBetweenDates = (calendar, date1, date2) => {
  let monthCount = calendar.get("months").count();
  if (date2.timestamp < date1.timestamp) {
    return -monthsBetweenDates(calendar, date2, date1);
  }

  if (date1.era === date2.era) {
    const months1 = calculateMonthsSinceEraStart(calendar, date1);
    const months2 = calculateMonthsSinceEraStart(calendar, date2);
    let diff = months2 - months1;
    if (diff !== 0) {
      if (getEraIsBackwards(calendar, date1.era)) {
        diff = -diff;
      }
      if (date2.day < date1.day) {
        --diff;
      } else if (date2.day === date1.day) {
        if (date2.hour < date1.hour) {
          --diff;
        } else if (date2.hour === date1.hour) {
          if (date2.minute < date1.minute) {
            --diff;
          } else if (date2.minute === date1.minute) {
            if (date2.second < date1.second) {
              --diff;
            }
          }
        }
      }
    }
    return diff;
  }

  // If we reach here, date2 must be a later era than date1
  // add months to get to start of year
  let diff = -(date1.month - 1);
  // add years to end of era
  if (getEraIsBackwards(calendar, date1.era)) {
    diff += date1.year * monthCount;
  } else {
    diff += (getEraDuration(calendar, date1.era) - date1.year + 1) * monthCount;
  }
  if (date1.day !== 1 || date1.hour !== 0 || date1.minute !== 0 || date1.second !== 0) {
    --diff;
  }
  // add any eras in the middle
  for (let i = date1.era + 1; i < date2.era; i++) {
    diff += getEraDuration(calendar, i) * monthCount;
  }
  // add the years so far in this era
  if (getEraIsBackwards(calendar, date2.era)) {
    diff += (getEraDuration(calendar, date2.era) - date2.year) * monthCount;
  } else {
    diff += (date2.year - 1) * monthCount;
  }
  // add the months so far into this year
  diff += date2.month - 1;

  for (let i = 0; i < 2; i++) {
    const span = createFromJS.TimespanRecord({ months: diff + 1 });
    const result = dateByAddingSpan(calendar, date1, span);
    if (result.timestamp > date2.timestamp) {
      break;
    }
    diff++;
  }
  return diff;
};

const weeksBetweenDates = (calendar, date1, date2) => {
  return floatToInt(
    secondsBetweenDates(date1, date2) / (calendar.get("weekdays").count() * calendar.get("hoursInDay") * 3600)
  );
};

const daysBetweenDates = (calendar, date1, date2) =>
  floatToInt(secondsBetweenDates(date1, date2) / (calendar.get("hoursInDay") * 3600));
const hoursBetweenDates = (date1, date2) => floatToInt(secondsBetweenDates(date1, date2) / 3600);
const minutesBetweenDates = (date1, date2) => floatToInt(secondsBetweenDates(date1, date2) / 60);
const secondsBetweenDates = (date1, date2) => {
  return secondsBetweenTS(date1.get("timestamp"), date2.get("timestamp"));
};

export default {
  weeksBetweenTS,
  daysBetweenTS,
  hoursBetweenTS,
  minutesBetweenTS,
  secondsBetweenTS,
  timespanBetweenDates,
  unitsBetweenDates,
  yearsBetweenDates,
  monthsBetweenDates,
  weeksBetweenDates,
  daysBetweenDates,
  hoursBetweenDates,
  minutesBetweenDates,
  secondsBetweenDates
};
